import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ChatBubble = ({ message, onSuggestionClick, onFeedback, onRetry }) => {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(message.feedback || null);
  const [showActions, setShowActions] = useState(false);

  const isAssistant = message.role === 'assistant';
  const isError = message.content?.startsWith('⚠️');
  const alignment = isAssistant ? 'items-start' : 'items-end';
  const bubbleClasses = isAssistant
    ? isError
      ? 'bg-red-50 border border-red-200 text-red-800'
      : 'bg-white border border-blue-100 text-gray-800'
    : 'bg-blue-600 text-white shadow-lg';

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [message.content]);

  const handleFeedback = useCallback((type) => {
    const newFeedback = feedback === type ? null : type;
    setFeedback(newFeedback);
    if (onFeedback) {
      onFeedback(message.id, newFeedback);
    }
  }, [feedback, message.id, onFeedback]);

  return (
    <div
      className={`flex flex-col ${alignment} w-full group`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {message.content && (
        <div className="relative">
          <div className={`w-full max-w-[85%] sm:max-w-xl rounded-2xl px-4 py-3 leading-relaxed prose prose-sm sm:prose-base prose-p:my-1 prose-p:last:mb-0 prose-ul:my-2 prose-li:my-0 ${bubbleClasses}`}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ node, ...props }) => <p {...props} className="mb-2 last:mb-0 break-words" />,
                ul: ({ node, className = '', ...props }) => (
                  <ul {...props} className={`list-disc pl-5 space-y-1 mb-2 ${className}`} />
                ),
                ol: ({ node, className = '', ...props }) => (
                  <ol {...props} className={`list-decimal pl-5 space-y-1 mb-2 ${className}`} />
                ),
                li: ({ node, className = '', ...props }) => (
                  <li {...props} className={`leading-snug ${className}`} />
                ),
                strong: ({ node, ...props }) => <strong {...props} className="font-semibold break-words" />,
                code: ({ node, ...props }) => <code {...props} className="break-all" />,
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>

          {/* Action buttons - show on hover for assistant messages */}
          {isAssistant && message.id !== 'initial' && (
            <div className={`flex items-center gap-1 mt-1 transition-opacity duration-200 ${showActions ? 'opacity-100' : 'opacity-0'}`}>
              {/* Copy button */}
              <button
                onClick={handleCopy}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title={copied ? 'Copied!' : 'Copy message'}
              >
                {copied ? (
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>

              {/* Thumbs up */}
              <button
                onClick={() => handleFeedback('positive')}
                className={`p-1.5 rounded-lg transition-colors ${feedback === 'positive'
                    ? 'text-green-600 bg-green-50'
                    : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                  }`}
                title="Helpful"
              >
                <svg className="w-4 h-4" fill={feedback === 'positive' ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
              </button>

              {/* Thumbs down */}
              <button
                onClick={() => handleFeedback('negative')}
                className={`p-1.5 rounded-lg transition-colors ${feedback === 'negative'
                    ? 'text-red-600 bg-red-50'
                    : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                  }`}
                title="Not helpful"
              >
                <svg className="w-4 h-4" fill={feedback === 'negative' ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                </svg>
              </button>

              {/* Retry button for errors */}
              {isError && onRetry && (
                <button
                  onClick={() => onRetry(message)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                  title="Retry"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {Array.isArray(message.cards) && message.cards.length > 0 && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 w-full max-w-3xl">
          {message.cards.map((card, idx) => (
            <div key={idx} className="border border-gray-200 rounded-xl bg-white shadow-sm p-3 sm:p-4 touch-manipulation">
              <div className="flex items-start justify-between gap-2">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 leading-tight">{card.title}</h4>
                {card.price && (
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 whitespace-nowrap flex-shrink-0">{card.price}</span>
                )}
              </div>
              {card.subtitle && <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">{card.subtitle}</p>}
              {Array.isArray(card.perks) && card.perks.length > 0 && (
                <div className="mt-2 sm:mt-3 flex flex-wrap gap-1.5 sm:gap-2">
                  {card.perks.map((perk, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200 break-words">{perk}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {message.suggestions && (
        <div className="mt-2 sm:mt-3 flex flex-wrap gap-2">
          {message.suggestions.map((suggestion, index) => (
            <button
              key={index}
              className="text-sm px-3 py-1.5 rounded-full border border-blue-300 bg-blue-50 text-blue-900 hover:bg-blue-100 transition touch-manipulation"
              onClick={() => onSuggestionClick(suggestion)}
            >
              {suggestion.label || suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatBubble;

